import React, { useState, useEffect, startTransition } from 'react';
import {
    Button,
    Box,
    CircularProgress,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Chip,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    IconButton,
    Container
} from '@mui/material';
import { Save, Delete, Add } from '@mui/icons-material';
import { useSnackbar } from 'src/view/components/SnackbarService';
import clientService from 'src/clientService';
import { AppSchemasUserUserResponse, UserRole, UserStatus, Assignment } from 'src/api/Api';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useParams, useNavigate } from 'react-router-dom';
import AssignProjectDialog from './AssignProjectDialog';
import { useAppState } from 'src/state-management/state-provider';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import DeveloperPermissionManager from './DeveloperPermissionManager';

const UserUpdatePage: React.FC = () => {
    const { dispatch } = useAppState();
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { showSnackbar, snackbarType } = useSnackbar();

    const [user, setUser] = useState<AppSchemasUserUserResponse | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<UserStatus | ''>('');
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [isLoadingAssignments, setIsLoadingAssignments] = useState<boolean>(false);
    const [isLoadingUser, setIsLoadingUser] = useState<boolean>(false);
    const [openAssignDialog, setOpenAssignDialog] = useState<boolean>(false);
    const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState<boolean>(false);
    const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);

    const fetchUser = async () => {
        if (userId) {
            setIsLoadingUser(true);
            try {
                const response = await clientService.api.getUserByIdApiV1AdminUsersUserIdGet(userId);
                setUser(response.data);
                setSelectedRoles(response.data.role || []);
                setSelectedStatus(response.data.status as UserStatus || '');
            } catch (error) {
                showSnackbar({ message: 'Failed to fetch user data.', type: snackbarType.ERROR });
            } finally {
                setIsLoadingUser(false);
            }
        }
    };

    const fetchAssignments = async () => {
        if (userId) {
            setIsLoadingAssignments(true);
            try {
                const response = await clientService.api.getAssignmentsByUserApiV1AssignmentsUserUserIdGet(userId);
                setAssignments(response.data);
            } catch (error) {
                showSnackbar({ message: 'Failed to fetch assignments.', type: snackbarType.ERROR });
            } finally {
                setIsLoadingAssignments(false);
            }
        }
        // empty project to refresh in next visit
        startTransition(() => {
            dispatch({ type: 'ADMIN_SET_PROJECTS', payload: [] });
        });
    };

    useEffect(() => {
        fetchUser();
        fetchAssignments();
    }, [userId]);

    const handleRoleChange = (event: SelectChangeEvent<UserRole[]>) => {
        const { target: { value } } = event;
        setSelectedRoles(typeof value === 'string' ? value.split(',') as UserRole[] : value as UserRole[]);
    };

    const handleStatusChange = (event: SelectChangeEvent<UserStatus>) => {
        setSelectedStatus(event.target.value as UserStatus);
    };

    const handleSubmit = async () => {
        if (!userId) return;

        setIsSubmitting(true);
        try {
            await clientService.api.updateUserApiV1AdminUsersUserIdPatch(userId, {
                role: selectedRoles,
                status: selectedStatus as UserStatus,
            });
            showSnackbar({ message: 'User updated successfully!', type: snackbarType.SUCCESS });
            startTransition(() => {
                navigate('/admin/users');
            });
        } catch (error: any) {
            const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to update user.';
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = (assignmentId: string) => {
        setAssignmentToDelete(assignmentId);
        setOpenConfirmDeleteDialog(true);
    };

    const handleConfirmDeleteAssignment = async () => {
        if (assignmentToDelete) {
            try {
                await clientService.api.deleteAssignmentEndpointApiV1AssignmentsAssignmentIdDelete(assignmentToDelete);
                showSnackbar({ message: 'Assignment deleted successfully!', type: snackbarType.SUCCESS });
                fetchAssignments(); // Refresh the list
            } catch (error: any) {
                const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to delete assignment.';
                showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
            } finally {
                setOpenConfirmDeleteDialog(false);
                setAssignmentToDelete(null);
            }
        }
    };

    if (isLoadingUser || !user) {
        return <CircularProgress />;
    }

    const assignedProjectIds = assignments.map(a => a.project.project_id);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>Edit User</Typography>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="h6" component="div">
                        {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {user.email || 'N/A'}
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>Update Fields</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="role-select-label">Role</InputLabel>
                            <Select
                                labelId="role-select-label"
                                id="role-select"
                                multiple
                                value={selectedRoles}
                                onChange={handleRoleChange}
                                input={<OutlinedInput id="select-multiple-chip" label="Role" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} />
                                        ))}
                                    </Box>
                                )}
                                disabled={isSubmitting}
                            >
                                {Object.values(UserRole).map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {role}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="status-select-label">Status</InputLabel>
                            <Select
                                labelId="status-select-label"
                                id="status-select"
                                value={selectedStatus}
                                label="Status"
                                onChange={handleStatusChange}
                                disabled={isSubmitting}
                            >
                                {Object.values(UserStatus).map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={() => startTransition(() => navigate('/admin/users'))} disabled={isSubmitting} sx={{ mr: 1 }}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || !user}
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    >
                        {isSubmitting ? 'Updating...' : 'Update User'}
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2 }}>
                <Typography variant="h5" gutterBottom>Assigned Projects</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => setOpenAssignDialog(true)}>Assign Project</Button>
            </Box>

            {isLoadingAssignments ? (
                <CircularProgress />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Project Name</TableCell>
                                <TableCell>Developer</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {assignments.length > 0 ? (
                                assignments.map((assignment) => (
                                    <TableRow key={assignment.assignment_id}>
                                        <TableCell>{assignment.project.project_name}</TableCell>
                                        <TableCell>{assignment.project.project_developer}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => handleDeleteClick(assignment.assignment_id)} size="small">
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        No projects assigned.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {openAssignDialog && (
                <AssignProjectDialog
                    open={openAssignDialog}
                    onProjectAssigned={fetchAssignments}
                    userId={userId!}
                    userEmail={user.email}
                    assignedProjectIds={assignedProjectIds}
                    onClose={() => setOpenAssignDialog(false)}
                />
            )}

            {/* Developer Permissions Section */}
            {selectedRoles.includes(UserRole.Dev) && userId && (
                <DeveloperPermissionManager userId={userId} />
            )}

            <ConfirmationDialog
                open={openConfirmDeleteDialog}
                onCancel={() => setOpenConfirmDeleteDialog(false)}
                onConfirm={handleConfirmDeleteAssignment}
                title="Delete Assignment"
                content="Are you sure you want to delete this project assignment? This action cannot be undone."
            />
        </Container>
    );
};

export default UserUpdatePage;
